import clsx from "clsx";
import deburr from "lodash/deburr";
import groupBy from "lodash/groupBy";
import React, { useState } from "react";
import Highlighter from "react-highlight-words";
import { FilterDialogScreen } from "web/react/components/filter-redesign/filter-dialog";
import { FilterSection } from "web/react/components/filter-redesign/filter-section";
import { MenuItemCheckbox } from "web/react/components/filter-redesign/menu-item-checkbox";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useActiveFilters, useFilterSync } from "web/react/hooks/filters";
import { useFilterBack } from "web/react/hooks/filters/use-filter-back";
import { useLazyFilter } from "web/react/hooks/filters/use-lazy-filter";
import { FilterOptionSerializer } from "web/types/serializers";
import styles from "./filters-filter-search-screen.module.css";

interface FiltersFilterSearchScreenProps {
    facetName: string;
}
export function FiltersFilterSearchScreen({
    facetName,
}: FiltersFilterSearchScreenProps): JSX.Element {
    const { options, label } = useLazyFilter(facetName);
    const { onFilterBack } = useFilterBack();

    return (
        <FilterDialogScreen title={label} onBack={() => onFilterBack("/")}>
            <FacetSearch options={options} facetName={facetName} />
        </FilterDialogScreen>
    );
}

interface SearchInputProps {
    onChange?(value: string): void;
    value: string;
}

function SearchInput({ onChange, value }: SearchInputProps): JSX.Element {
    const [searchInputFocused, setSearchInputFocused] = useState(false);

    function handleFilter(event: React.ChangeEvent<HTMLInputElement>): any {
        onChange?.(event.target.value);
    }

    function onBlurSearchInput(): void {
        setSearchInputFocused(false);
    }

    return (
        <div
            className={clsx(styles.searchField, {
                [styles.searchFieldFocused]: searchInputFocused,
            })}
        >
            <div className={styles.searchIconBackground}>
                <SVGIcon name="search" className={styles.searchIcon} />
            </div>
            <input
                className={styles.input}
                type="text"
                onBlur={onBlurSearchInput}
                onChange={handleFilter}
                placeholder="Search"
                value={value}
            />
        </div>
    );
}

const REGEX_AZ = /([A-Z])/i;
const REGEX_NUMBER = /([0-9])/;

interface FacetSearchProps {
    options: FilterOptionSerializer[];
    facetName: string;
}

function FacetSearch({ options, facetName }: FacetSearchProps): JSX.Element {
    const { isActive } = useActiveFilters(facetName);
    const { onOptionChange } = useFilterSync();
    const [query, setQuery] = useState("");

    function getOptionGroupLabel(option): string {
        const firstCharacter = option.label.charAt(0).normalize("NFD")[0].toUpperCase();
        if (isActive(option)) {
            return " ";
        } else if (REGEX_AZ.test(firstCharacter)) {
            return firstCharacter;
        } else if (REGEX_NUMBER.test(firstCharacter)) {
            return "0-9";
        }
        return "!#";
    }

    const filteredOptions = options?.filter((option) =>
        deburr(option.label.toLowerCase()).includes(deburr(query.toLowerCase()))
    );
    const groupedBy = Object.entries(groupBy(options, getOptionGroupLabel));

    return (
        <div className={styles.background}>
            <SearchInput value={query} onChange={(value) => setQuery(value)} />
            {query !== "" ? (
                <FilterSection title="">
                    <ul>
                        {filteredOptions?.map((option) => (
                            <li key={option.id}>
                                <MenuItemCheckbox
                                    label={
                                        <Highlighter
                                            highlightClassName={styles.highlighted}
                                            searchWords={[query]}
                                            textToHighlight={option.label}
                                            autoEscape={true}
                                            sanitize={deburr}
                                        />
                                    }
                                    onChange={() => onOptionChange(option)}
                                    checked={isActive(option)}
                                    to={option.url}
                                />
                            </li>
                        ))}
                    </ul>
                </FilterSection>
            ) : (
                <>
                    {groupedBy.map(([firstLetter, options]) => (
                        <FilterSection title={firstLetter} key={firstLetter}>
                            <ul>
                                {options.map((option) => (
                                    <li key={option.label}>
                                        <MenuItemCheckbox
                                            label={option.label}
                                            onChange={() => onOptionChange(option)}
                                            checked={isActive(option)}
                                            to={option.url}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </FilterSection>
                    ))}
                </>
            )}
        </div>
    );
}
